// custom code start
/*
 * <header-search>: desktop header search bar (snippets/header-search.liquid).
 *
 * Attributes on the element drive the CSS:
 * - open        the panel and the white underlay are shown, page scroll is locked
 * - data-state  default (menu links + products) | loading (waiting for results) | results
 * - data-ready  set on first hover/open; until then the default panel is display:none
 *               so its lazy images do not load on every page view
 * - closing     set for two frames on close so the underlay disappears at once
 *
 * data-has-default="false" (no menu and no products set): an empty bar does not open
 * the panel; it opens once there is text to search.
 *
 * Results come from sections/header-search-results.liquid through the predictive
 * search URL (q, resource types, section_id), debounced 300ms, cached per term.
 * Predictive search does not support every language (Arabic answers HTTP 417), so a
 * failed request falls back to the same section rendered on the search page.
 */
if (!customElements.get('header-search')) {
  class HeaderSearch extends HTMLElement {
    static DEBOUNCE = 300;
    static LOCK_CLASS = 'header-search-open';
    // A horizontal wheel/trackpad gesture must travel this far before the carousel moves one item,
    // then further wheel events are ignored for the cooldown (one swipe = one item, not a fling).
    static WHEEL_THRESHOLD = 50;
    static WHEEL_COOLDOWN = 450;
    static WHEEL_RESET = 200;
    // Resting on a main-nav item this long while the panel is open closes the search,
    // so its megamenu (which opens under the panel) can be used. Crossing the nav does not.
    static NAV_INTENT = 200;

    constructor() {
      super();
      this.cache = new Map();
      this.carousels = new Set();
      this.term = '';
      this.isOpen = false;
      this.abortController = null;
      this.debounceTimer = null;
      this.resizeFrame = null;
      this.announceFrame = null;
      this.navTimer = null;
      this.navItem = null;
      this.skipFocusOpen = false;
      this.suggestUnsupported = false;
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onPageHide = this.onPageHide.bind(this);
      this.onNavOver = this.onNavOver.bind(this);
      this.onNavOut = this.onNavOut.bind(this);
      this.scrollPosition = window.scrollY;
    }

    connectedCallback() {
      this.form = this.querySelector('[data-form]');
      this.input = this.querySelector('[data-input]');
      this.defaultPanel = this.querySelector('[data-default-panel]');
      this.resultsContent = this.querySelector('[data-results-content]');
      this.status = this.querySelector('[data-status]');
      if (!this.form || !this.input || !this.defaultPanel || !this.resultsContent) return;

      this.hasDefaultContent = this.dataset.hasDefault !== 'false';

      this.input.addEventListener('focus', () => {
        if (!this.skipFocusOpen) this.open();
      });
      this.input.addEventListener('click', () => this.open());
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (event) => this.onInputKeydown(event));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('focusin', () => this.keepScrollPosition());
      this.addEventListener('focusout', (event) => this.onFocusOut(event));
      this.addEventListener('pointerenter', () => this.prepare(), { once: true });

      document.addEventListener('click', this.onDocumentClick);
      this.desktopQuery.addEventListener('change', this.onViewportChange);
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('pagehide', this.onPageHide);

      this.nav = this.closest('.header')?.querySelector('.js-nav') || null;
      this.nav?.addEventListener('mouseover', this.onNavOver);
      this.nav?.addEventListener('mouseout', this.onNavOut);

      this.defaultPanel.querySelectorAll('[data-carousel]').forEach((carousel) => this.initCarousel(carousel));
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      this.desktopQuery.removeEventListener('change', this.onViewportChange);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('pagehide', this.onPageHide);
      this.nav?.removeEventListener('mouseover', this.onNavOver);
      this.nav?.removeEventListener('mouseout', this.onNavOut);
      clearTimeout(this.navTimer);
      document.body.classList.remove(HeaderSearch.LOCK_CLASS);
    }

    get value() {
      return this.input.value.trim();
    }

    get scrollBehavior() {
      return this.reducedMotionQuery.matches ? 'auto' : 'smooth';
    }

    // Show the default panel in the layout (still invisible) so the open transition can run.
    prepare() {
      if (this.hasAttribute('data-ready')) return;
      this.setAttribute('data-ready', '');
      void this.defaultPanel.offsetHeight;
    }

    // load: false when the caller (typing) schedules its own debounced search.
    open({ load = true } = {}) {
      if (this.isOpen || !this.desktopQuery.matches) return;

      const value = this.value;
      // Nothing to show for an empty bar when neither the menu nor the collection is set.
      if (!value && !this.hasDefaultContent) return;

      this.prepare();
      this.isOpen = true;

      if (!value) {
        this.setState('default');
      } else if (value === this.term && this.resultsContent.childElementCount) {
        this.setState('results');
      } else {
        this.setState('loading');
        if (load) this.search(value);
      }

      this.removeAttribute('closing');
      this.setAttribute('open', '');
      document.body.classList.add(HeaderSearch.LOCK_CLASS);
      this.updateExpanded();
      requestAnimationFrame(() => this.updateCarousels());
    }

    close({ clear = false, blur = false } = {}) {
      clearTimeout(this.navTimer);
      this.navItem = null;

      if (clear) {
        clearTimeout(this.debounceTimer);
        this.abort();
        this.input.value = '';
        this.term = '';
        this.announce('');
      }

      if (this.isOpen) {
        this.isOpen = false;
        this.setAttribute('closing', '');
        this.removeAttribute('open');
        document.body.classList.remove(HeaderSearch.LOCK_CLASS);
        this.clearSelection();
        requestAnimationFrame(() => requestAnimationFrame(() => this.removeAttribute('closing')));
      }

      if (clear) this.setState('default');
      this.updateExpanded();
      if (blur && this.contains(document.activeElement)) document.activeElement.blur();
    }

    setState(state) {
      this.dataset.state = state;
      this.updateExpanded();
    }

    // The input controls the results listbox, so it is "expanded" only while results show.
    // The default panel is reached with Tab like any other content.
    updateExpanded() {
      this.input.setAttribute('aria-expanded', String(this.isOpen && this.dataset.state === 'results'));
    }

    // Clear the live region first so the same text (same term again, same count) is announced again.
    announce(text) {
      if (!this.status) return;
      cancelAnimationFrame(this.announceFrame);
      this.status.textContent = '';
      if (!text) return;
      this.announceFrame = requestAnimationFrame(() => {
        this.status.textContent = text;
      });
    }

    onInput() {
      const value = this.value;
      clearTimeout(this.debounceTimer);
      this.clearSelection();

      if (!value) {
        this.abort();
        this.term = '';
        this.announce('');
        this.setState('default');
        if (!this.hasDefaultContent) {
          this.close();
          return;
        }
        if (!this.isOpen && document.activeElement === this.input) this.open();
        return;
      }

      if (!this.isOpen) this.open({ load: false });
      if (this.dataset.state === 'default') this.setState('loading');
      this.debounceTimer = setTimeout(() => this.search(value), HeaderSearch.DEBOUNCE);
    }

    onInputKeydown(event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!this.isOpen) {
          this.open();
          return;
        }
        if (this.dataset.state !== 'results') return;
        event.preventDefault();
        this.moveSelection(event.key === 'ArrowDown' ? 1 : -1);
        return;
      }

      if (event.key === 'Enter') {
        const selected = this.getSelectedOption();
        if (!selected) return;
        event.preventDefault();
        selected.querySelector('a')?.click();
      }
    }

    // Escape with text clears it and shows the default panel; Escape on an empty bar closes.
    onKeydown(event) {
      if (event.key !== 'Escape') return;

      if (this.input.value) {
        event.preventDefault();
        this.input.value = '';
        this.onInput();
        this.focusInput();
        if (!this.isOpen) this.open();
        return;
      }

      if (!this.isOpen) return;
      event.preventDefault();
      this.close();
      this.focusInput();
    }

    onSubmit(event) {
      if (!this.value || this.getSelectedOption()) event.preventDefault();
    }

    onClick(event) {
      const target = event.target;
      const closeButton = target.closest('[data-close]');
      if (closeButton) {
        this.close({ clear: true, blur: true });
        return;
      }

      const arrow = target.closest('[data-arrow]');
      if (arrow) {
        this.scrollCarousel(arrow);
        return;
      }

      const tab = target.closest('[data-tab]');
      if (tab) this.switchTab(tab);
    }

    onDocumentClick(event) {
      if (this.isOpen && !this.contains(event.target)) this.close();
    }

    onFocusOut(event) {
      if (this.isOpen && event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
    }

    onViewportChange(event) {
      if (!event.matches) this.close();
    }

    // Leaving the page (a result or menu link) must not store an open panel and a locked page
    // in the back/forward cache.
    onPageHide() {
      this.close();
    }

    // The megamenu opens on hover (global.js) below the header, under this panel.
    // If the pointer rests on a nav item and its dropdown is open, close the search.
    onNavOver(event) {
      if (!this.isOpen) return;
      const item = event.target.closest?.('.js-nav-item[data-menu-item]');
      if (!item || item === this.navItem || !item.querySelector('.js-dropdown')) return;

      this.navItem = item;
      clearTimeout(this.navTimer);
      this.navTimer = setTimeout(() => {
        if (this.isOpen && this.navItem === item && item.querySelector('.js-dropdown.is-visible')) this.close();
        this.navItem = null;
      }, HeaderSearch.NAV_INTENT);
    }

    onNavOut(event) {
      if (!this.navItem || this.navItem.contains(event.relatedTarget)) return;
      clearTimeout(this.navTimer);
      this.navItem = null;
    }

    onResize() {
      if (this.resizeFrame) return;
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateCarousels();
      });
    }

    onScroll() {
      this.scrollPosition = window.scrollY;
    }

    // html has scroll-padding-block-start: var(--header-height), so focusing a control in the
    // sticky header makes the browser scroll the page up to "reveal" it. Put the page back.
    keepScrollPosition() {
      if (window.scrollY !== this.scrollPosition) window.scrollTo({ top: this.scrollPosition, behavior: 'instant' });
    }

    focusInput() {
      if (document.activeElement === this.input) return;
      this.skipFocusOpen = true;
      this.input.focus();
      this.skipFocusOpen = false;
    }

    abort() {
      this.abortController?.abort();
      this.abortController = null;
      this.removeAttribute('loading');
    }

    getSuggestUrl(term) {
      // Types spelled out: this store's default omits `query`, which the UK gets by default (the <mark> suggestions).
      return `${this.dataset.suggestUrl}?q=${encodeURIComponent(term)}&resources%5Btype%5D=query,product,collection,page&resources%5Blimit%5D=10&section_id=${encodeURIComponent(this.dataset.sectionId)}`;
    }

    // Same section on the search page: products and pages from the `search` object.
    getFallbackUrl(term) {
      return `${this.dataset.searchUrl}?q=${encodeURIComponent(term)}&type=product,page&options%5Bprefix%5D=last&section_id=${encodeURIComponent(this.dataset.sectionId)}`;
    }

    async readSection(response) {
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = html.querySelector('.shopify-section');
      if (!section) throw new Error('Missing section');
      return section.innerHTML;
    }

    async search(term) {
      const key = term.toLowerCase();
      if (this.cache.has(key)) {
        this.render(term, this.cache.get(key));
        return;
      }

      this.abort();
      const controller = new AbortController();
      this.abortController = controller;
      this.setAttribute('loading', '');

      try {
        let markup = null;

        if (!this.suggestUnsupported) {
          const response = await fetch(this.getSuggestUrl(term), { signal: controller.signal });
          if (response.ok) {
            markup = await this.readSection(response);
          } else if (response.status === 417) {
            // "Unsupported buyer locale": this language never gets predictive results.
            this.suggestUnsupported = true;
          }
        }

        if (markup === null) {
          const response = await fetch(this.getFallbackUrl(term), { signal: controller.signal });
          if (!response.ok) throw new Error(response.status);
          markup = await this.readSection(response);
        }

        this.cache.set(key, markup);
        this.render(term, markup);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.renderError(term);
      } finally {
        if (this.abortController === controller) {
          this.abortController = null;
          this.removeAttribute('loading');
        }
      }
    }

    render(term, markup) {
      // Ignore a response for a term the visitor has already changed.
      if (term !== this.value) return;

      this.term = term;
      this.resultsContent.innerHTML = markup;
      this.clearSelection();
      this.resultsContent.querySelectorAll('[data-carousel]').forEach((carousel) => this.initCarousel(carousel));

      this.announce(this.resultsContent.querySelector('[data-live-count]')?.textContent.trim() || '');

      if (this.isOpen) this.setState('results');
      requestAnimationFrame(() => this.updateCarousels());
    }

    // Both requests failed: replace whatever showed with a message for this term. Not cached,
    // and the term is forgotten so focusing the bar again retries.
    renderError(term) {
      if (term !== this.value) return;

      const message = (this.dataset.errorText || '').replace('[terms]', term);
      const wrapper = document.createElement('div');
      const text = document.createElement('p');
      text.className = 'header-search__no-results';
      text.textContent = message;
      const count = document.createElement('span');
      count.hidden = true;
      count.dataset.liveCount = '';
      count.textContent = message;
      wrapper.append(text, count);

      this.render(term, wrapper.innerHTML);
      this.term = '';
    }

    getOptions() {
      return Array.from(this.resultsContent.querySelectorAll('[role="option"]')).filter(
        (option) => option.offsetParent !== null
      );
    }

    getSelectedOption() {
      if (this.dataset.state !== 'results') return null;
      return this.resultsContent.querySelector('[role="option"][aria-selected="true"]');
    }

    moveSelection(step) {
      const options = this.getOptions();
      if (!options.length) return;

      const current = this.getSelectedOption();
      let index = options.indexOf(current);
      if (index === -1) {
        if (step < 0) return;
        index = 0;
      } else {
        index = (index + step + options.length) % options.length;
      }

      const option = options[index];
      current?.setAttribute('aria-selected', 'false');
      option.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', option.id);

      const list = option.closest('[data-list]');
      if (list) {
        this.revealInList(option, list);
      } else {
        this.revealInPanel(option);
      }
    }

    clearSelection() {
      this.resultsContent
        .querySelectorAll('[role="option"][aria-selected="true"]')
        .forEach((option) => option.setAttribute('aria-selected', 'false'));
      this.input.removeAttribute('aria-activedescendant');
    }

    switchTab(tab) {
      const root = tab.closest('[data-tabs]');
      if (!root) return;

      root.querySelectorAll('[data-tab]').forEach((item) => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
      });
      root.querySelectorAll('[data-tab-panel]').forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.tabPanel === tab.dataset.tab);
      });

      this.clearSelection();
      this.updateCarousels();
    }

    isRtl(element) {
      return getComputedStyle(element).direction === 'rtl';
    }

    // One item width plus the gap: the distance between two snap points.
    getStep(list) {
      const item = list.firstElementChild;
      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      return item ? item.getBoundingClientRect().width + gap : list.clientWidth;
    }

    // Scroll a carousel list to the nearest snap position that shows the whole item holding
    // `element` (keyboard selection, Tab focus). Works in both directions.
    revealInList(element, list) {
      const item = Array.from(list.children).find((child) => child.contains(element));
      if (!item) return;

      const listRect = list.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      if (itemRect.left >= listRect.left - 1 && itemRect.right <= listRect.right + 1) return;

      const rtl = this.isRtl(list);
      const position = Math.abs(list.scrollLeft);
      const start = (rtl ? listRect.right - itemRect.right : itemRect.left - listRect.left) + position;
      const max = list.scrollWidth - list.clientWidth;
      const step = this.getStep(list);

      let target = start;
      if (start >= position) {
        // Clipped at the end: the first snap position where the item's end is inside the list.
        target = Math.ceil((start + itemRect.width - list.clientWidth - 1) / step) * step;
      }
      target = Math.max(0, Math.min(max, target));

      list.scrollTo({ left: rtl ? -target : target, behavior: this.scrollBehavior });
    }

    // Vertical: keep a selected pill inside the panel when the panel scrolls (long pages list).
    revealInPanel(element) {
      const panel = element.closest('.header-search__panel');
      if (!panel) return;

      const panelRect = panel.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      let delta = 0;
      if (rect.top < panelRect.top) delta = rect.top - panelRect.top;
      else if (rect.bottom > panelRect.bottom) delta = rect.bottom - panelRect.bottom;
      if (delta) panel.scrollBy({ top: delta, behavior: this.scrollBehavior });
    }

    // Native horizontal scroll with snap points; the arrows move one item (width + gap) at a time.
    initCarousel(carousel) {
      const list = carousel.querySelector('[data-list]');
      if (!list) return;

      const prev = carousel.querySelector('[data-arrow="prev"]');
      const next = carousel.querySelector('[data-arrow="next"]');
      const setDisabled = (button, disabled) => {
        if (!button) return;
        button.classList.toggle('is-disabled', disabled);
        button.setAttribute('aria-disabled', String(disabled));
      };

      carousel.updateArrows = () => {
        const max = list.scrollWidth - list.clientWidth;
        // scrollLeft is negative in RTL; the distance from the start is its absolute value.
        const position = Math.abs(list.scrollLeft);
        setDisabled(prev, max <= 0 || position <= 4);
        setDisabled(next, max <= 0 || position >= max - 4);
      };

      list.addEventListener('scroll', carousel.updateArrows, { passive: true });

      // Tab into a card that the list clips: bring the card fully into view.
      list.addEventListener('focusin', (event) => this.revealInList(event.target, list));

      // Horizontal wheel / trackpad: collect the deltas and move one item per gesture.
      let wheelDelta = 0;
      let wheelLast = 0;
      let wheelLockedUntil = 0;
      list.addEventListener(
        'wheel',
        (event) => {
          if (list.scrollWidth - list.clientWidth <= 0) return;
          if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
          event.preventDefault();

          const now = event.timeStamp;
          if (now - wheelLast > HeaderSearch.WHEEL_RESET) wheelDelta = 0;
          wheelLast = now;
          if (now < wheelLockedUntil) return;

          wheelDelta += event.deltaMode === 1 ? event.deltaX * 16 : event.deltaX;
          if (Math.abs(wheelDelta) < HeaderSearch.WHEEL_THRESHOLD) return;

          // deltaX is physical (positive = right) in both directions, like scrollBy.
          list.scrollBy({ left: this.getStep(list) * Math.sign(wheelDelta), behavior: this.scrollBehavior });
          wheelDelta = 0;
          wheelLockedUntil = now + HeaderSearch.WHEEL_COOLDOWN;
        },
        { passive: false }
      );

      this.carousels.add(carousel);
      carousel.updateArrows();
    }

    scrollCarousel(arrow) {
      if (arrow.getAttribute('aria-disabled') === 'true') return;
      const list = arrow.closest('[data-carousel]')?.querySelector('[data-list]');
      if (!list) return;

      const direction = arrow.dataset.arrow === 'prev' ? -1 : 1;
      const rtl = this.isRtl(list) ? -1 : 1;
      list.scrollBy({ left: this.getStep(list) * direction * rtl, behavior: this.scrollBehavior });
    }

    updateCarousels() {
      this.carousels.forEach((carousel) => {
        if (!carousel.isConnected) {
          this.carousels.delete(carousel);
          return;
        }
        carousel.updateArrows();
      });
    }
  }

  customElements.define('header-search', HeaderSearch);
}
// custom code end
